import domainStructures from './domain-structures.json';
import type { DomainSchema, FieldDef, FieldType } from './types';

// ─── Data-type → UI field type ────────────────────────────────────────────────

// LinkedIn DMA date unions can appear in either order (e.g. string|number, number|string).
const MIXED_DATE_DATA_TYPES = new Set(['string|number', 'number|string']);

/**
 * Converts a LinkedIn DMA data-type string to the nearest appropriate UI
 * `FieldType`. Used when auto-building schemas for non-priority domains.
 */
function toFieldType(dataType: string): FieldType {
  if (dataType === 'boolean') return 'checkbox';
  if (dataType === 'number') return 'number';
  // Keep both variants in sync so date-like unions map consistently.
  if (MIXED_DATE_DATA_TYPES.has(dataType)) return 'date';
  // Textarea for free-form multi-line fields
  return 'text';
}

// ─── Card title inference ─────────────────────────────────────────────────────

/** Field names that make good card titles, checked in priority order. */
const TITLE_CANDIDATES = [
  'title',
  'name',
  'fullName',
  'headline',
  'eventName',
  'groupName',
  'productName',
  'jobTitle',
  'courseName',
  'skillName',
  'companyName',
  'organizationName',
  'testName',
  'causeName',
  'questionText',
];

function inferCardTitle(fieldNames: string[]): string | null {
  const fieldSet = new Set(fieldNames);
  const match = TITLE_CANDIDATES.find(c => fieldSet.has(c));
  return match ? `{${match}}` : null;
}

// ─── Schema builder ───────────────────────────────────────────────────────────

/**
 * Builds a minimal `DomainSchema` for a non-priority domain using the
 * inferred field definitions from `domain-structures.json`.
 *
 * The resulting schema has:
 * - `priority: false`
 * - Auto-inferred `cardTitle` from known title-candidate fields, or `null`
 * - All fields mapped to nearest UI type, one field per layout row
 */
export function buildMinimalSchema(domainName: string): DomainSchema | undefined {
  const def = (domainStructures.domains as Record<string, { record: Record<string, string> }>)[
    domainName
  ];
  if (!def?.record) return undefined;

  const fieldEntries = Object.entries(def.record);
  const fieldNames = fieldEntries.map(([f]) => f);

  const fields: Record<string, FieldDef> = {};
  for (const [fieldName, dataType] of fieldEntries) {
    fields[fieldName] = { type: toFieldType(dataType) };
  }

  const cardTitle = inferCardTitle(fieldNames);

  // Layout: one field per row. Title-candidate field is still included in
  // layout since this schema has no curated design — consistency over concision.
  const layout = fieldNames.map(field => [{ field }]);

  return {
    priority: false,
    cardTitle,
    fields,
    layout,
  } satisfies DomainSchema;
}
